import { useState, useRef } from "react";
import { Helmet } from "react-helmet";
import { useForm } from "@formspree/react";
import { ArrowRight, Play, Square } from "lucide-react";
import { trackEvent } from "../../helpers";
import {
  PageWrapper,
  Main,
  HeroSection,
  CopyColumn,
  Eyebrow,
  HeroTitle,
  HeroSubtitle,
  Form,
  FormRow,
  Input,
  SubmitButton,
  FineText,
  SuccessMessage,
  ProofBar,
  StageColumn,
  PhoneMockup,
  PhoneScreen,
  VideoBadge,
  SoundButton,
} from "./ai-marketing.styles";

export const AIMarketing = () => {
  const [state, handleSubmit] = useForm("xqpekvze");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isFullEpisode, setIsFullEpisode] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const isDisabled = !name || !email;

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    trackEvent("AI Drama - Waitlist Signup", { name, email });
    await handleSubmit(e);
  };

  const toggleVideo = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isFullEpisode) {
      video.src = "/ai-drama/loop.mp4";
      video.loop = true;
      video.muted = true;
      video.controls = false;
      setIsFullEpisode(false);
    } else {
      video.src = "/ai-drama/episode.mp4";
      video.loop = false;
      video.muted = false;
      video.controls = true;
      setIsFullEpisode(true);
    }
    video.play().catch(() => {});
  };

  const handleVideoEnded = () => {
    if (isFullEpisode) {
      toggleVideo();
    }
  };

  return (
    <PageWrapper>
      <Helmet>
        <title>AI Drama Course | Renee Louise</title>
        <meta
          name="description"
          content="Learn to make AI short dramas that look like real TV. Join the waitlist for the AI Drama Course and get founding-member pricing."
        />
        <link rel="canonical" href="https://reneelouise.tech/ai-drama" />
      </Helmet>

      <Main>
        <HeroSection>
          <CopyColumn>
            <Eyebrow>Coming Soon</Eyebrow>
            <HeroTitle>
              Make AI short dramas that look like <em>real TV</em>
            </HeroTitle>
            <HeroSubtitle>
              Learn to make vertical micro-drama series with characters who look
              the same in every shot, scenes that hold together and cliffhangers
              people come back for.
            </HeroSubtitle>

            {state.succeeded ? (
              <SuccessMessage>
                You're on the list. Watch your inbox for early access.
              </SuccessMessage>
            ) : (
              <Form onSubmit={onSubmit}>
                <FormRow>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Your name"
                    autoComplete="given-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </FormRow>
                <input type="hidden" name="source" value="ai-drama-waitlist" />
                <SubmitButton
                  type="submit"
                  $isDisabled={isDisabled || state.submitting}
                  disabled={isDisabled || state.submitting}
                >
                  {state.submitting ? "Joining..." : "Join the waitlist"}
                  <ArrowRight size={16} color="#fff" />
                </SubmitButton>
                <FineText>
                  <strong>Founding-member pricing</strong> for everyone on the
                  list. No spam, unsubscribe any time.
                </FineText>
              </Form>
            )}

            <ProofBar>
              <span>
                <b>10+</b> modules
              </span>
              <span>
                <b>Every</b> template included
              </span>
            </ProofBar>
          </CopyColumn>

          <StageColumn>
            <PhoneMockup>
              <PhoneScreen>
                <video
                  ref={videoRef}
                  src="/ai-drama/loop.mp4"
                  poster="/ai-drama/poster.jpg"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  aria-label="AI micro-drama preview"
                  onEnded={handleVideoEnded}
                />
                <VideoBadge>Made with AI</VideoBadge>
              </PhoneScreen>
            </PhoneMockup>
            <SoundButton type="button" onClick={toggleVideo}>
              {isFullEpisode ? (
                <Square size={14} fill="#f3ede4" />
              ) : (
                <Play size={14} fill="#f3ede4" />
              )}
              <span>
                {isFullEpisode
                  ? "Back to the silent loop"
                  : "Watch with sound"}
              </span>
            </SoundButton>
          </StageColumn>
        </HeroSection>
      </Main>
    </PageWrapper>
  );
};
